import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { useGetNewestPressReleasesQuery } from '@/api/sanity/generated';
import { Layout } from '@/components/layout';
import { LINKS } from '@/constants/links';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { usePageView } from '@/analytics/pageTypes';
import { PressRelease, PressReleaseDetail } from '@/types/pressRelease';
import { Faq } from '@/sections/aboutUs/Faq';
import { MoreInfo } from '@/sections/aboutUs/MoreInfo';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { DownloadButton } from '@/components/buttons';
import { HeroWithBreadcrumbs } from '@/sections/HeroWithBreadcrumbs';
import { useTranslation } from 'react-i18next';
import { getExtension } from '@/utils/contentType';
import { SeoHeader } from '@/components/SeoHeader';
import IconCalendar from '@/assets/icons/heroIcons/Calendar.svg';
import dayjs from 'dayjs';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { sanityLanguage } from '@/constants/language';
import { MarkdownContent } from '@/components/Markdown/Markdown';
import { getPressReleaseDetail } from '@/sections/pressReleases/functions/getPressReleaseDetail';
interface PageProps extends IntlProps {
  pressReleaseDetail: PressReleaseDetail;
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function Page(props) {
  const $ = _c(68);
  const {
    pressReleaseDetail,
    menuPromoBanners
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["forJournalists", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const t1 = pressReleaseDetail?.name || "";
  let t2;
  if ($[1] !== pressReleaseDetail.slug?.current) {
    t2 = LINKS().careerDetail(pressReleaseDetail?.slug?.current || "");
    $[1] = pressReleaseDetail.slug?.current;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      title: t1,
      url: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  usePageView("career-detail", t3);
  let t4;
  if ($[6] !== t) {
    t4 = t("seoDetail.title");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("seoDetail.description");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <SeoHeader title={t4} description={t5} />;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="bgCircle absolute top-0 left-2/3 z-10 hidden h-[73rem] w-[73rem] -translate-y-1/3 rotate-180 lg:block" />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t("title");
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = LINKS();
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8) {
    t10 = {
      title: t8,
      to: t9.forMedia
    };
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t("pressReleasesSection.title");
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = LINKS();
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11) {
    t13 = {
      title: t11,
      to: t12.pressReleases
    };
    $[22] = t11;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t) {
    t14 = t("pressReleasesDetail.title");
    $[24] = t;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14) {
    t15 = {
      title: t14
    };
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t10 || $[29] !== t13 || $[30] !== t15) {
    t16 = [t10, t13, t15];
    $[28] = t10;
    $[29] = t13;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== pressReleaseDetail.name) {
    t17 = <h1 className="mx-auto mb-4 max-w-192 text-center text-2xl font-bold text-white lg:mb-6 lg:text-4xl">{pressReleaseDetail.name}</h1>;
    $[32] = pressReleaseDetail.name;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <IconCalendar className="-mt-0.5 h-4 w-4 md:h-6 md:w-6" />;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== pressReleaseDetail.date) {
    t19 = dayjs(pressReleaseDetail.date as string).format("DD.MM.YYYY");
    $[35] = pressReleaseDetail.date;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t19) {
    t20 = <div className="flex flex-col items-center justify-center gap-2 text-sm text-white md:flex-row lg:gap-6 lg:text-base"><div className="flex flex-row items-center justify-center gap-1">{t18}<p>{t19}</p></div></div>;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t16 || $[40] !== t17 || $[41] !== t20) {
    t21 = <HeroWithBreadcrumbs breadcrumbs={t16} contentClassName="max-w-[62.5rem]">{t17}{t20}</HeroWithBreadcrumbs>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="bg-blue-900"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== pressReleaseDetail.perex) {
    t23 = pressReleaseDetail?.perex && <p className="text-sm lg:text-base">{pressReleaseDetail.perex}</p>;
    $[44] = pressReleaseDetail.perex;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== pressReleaseDetail.textMarkdown) {
    t24 = pressReleaseDetail?.textMarkdown && <MarkdownContent content={pressReleaseDetail.textMarkdown} />;
    $[46] = pressReleaseDetail.textMarkdown;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== pressReleaseDetail.aboutUsSection || $[49] !== t) {
    t25 = pressReleaseDetail.aboutUsSection && <div><h2 className="mb-3 text-lg font-bold lg:mb-4 lg:text-2xl">{t("pressReleasesDetail.aboutCompany.title")}</h2><p className="text-sm lg:text-base">{t("pressReleasesDetail.aboutCompany.text")}</p></div>;
    $[48] = pressReleaseDetail.aboutUsSection;
    $[49] = t;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== pressReleaseDetail.file || $[52] !== t) {
    t26 = pressReleaseDetail?.file && pressReleaseDetail.file.asset?.url && pressReleaseDetail.file.asset.mimeType && <div><h2 className="mb-3 font-semibold lg:mb-4">{t("pressReleasesDetail.download.title")}</h2><DownloadButton to={pressReleaseDetail.file.asset.url} testId="pressReleaseFile" analyticsParam="pressReleaseFile">{t("pressReleasesDetail.download.file")}{getExtension(pressReleaseDetail.file.asset.mimeType) && ` (.${getExtension(pressReleaseDetail.file.asset.mimeType)})`}</DownloadButton></div>;
    $[51] = pressReleaseDetail.file;
    $[52] = t;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== t23 || $[55] !== t24 || $[56] !== t25 || $[57] !== t26) {
    t27 = <div className="relative z-30 bg-blue-100 px-4 pt-12 pb-20 lg:pt-0 lg:pb-40"><div className="mx-auto flex max-w-192 flex-col gap-12 whitespace-normal lg:gap-16">{t23}{t24}{t25}{t26}</div></div>;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="relative overflow-hidden bg-blue-50 py-10 lg:py-20"><div className="relative z-10 flex flex-col gap-20 lg:gap-40"><Faq /><MoreInfo /></div><div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" /></div>;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t21 || $[62] !== t27) {
    t30 = <div className="relative overflow-x-hidden">{t7}{t21}{t22}{t27}{t28}{t29}</div>;
    $[61] = t21;
    $[62] = t27;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== menuPromoBanners || $[65] !== t30 || $[66] !== t6) {
    t31 = <Layout variant="inverted" menuPromoBanners={menuPromoBanners}>{t6}{t30}</Layout>;
    $[64] = menuPromoBanners;
    $[65] = t30;
    $[66] = t6;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  return t31;
}
export const getStaticPaths = async () => {
  let offset = 0;
  const maxSkip = 200;
  const result: NonNullable<PressRelease>[] = [];
  while (true) {
    if (offset > maxSkip) {
      break;
    }
    const pressReleases = await useGetNewestPressReleasesQuery.fetcher({
      offset,
      i18nLang: sanityLanguage
    })();
    if (!pressReleases?.allPressRelease?.length) {
      break;
    }
    if (pressReleases.allPressRelease) {
      result.push(...pressReleases.allPressRelease.filter((item): item is NonNullable<typeof item> => !!item && !!item.name && !!item.slug?.current).map(item => item as NonNullable<PressRelease>));
    }
    offset += pressReleases.allPressRelease.length;
  }
  const paths = result.map(pressRelease => {
    if (!pressRelease.slug?.current) {
      return null;
    }
    return {
      params: {
        pressRelease: pressRelease.slug.current
      }
    };
  }).filter(Boolean);
  return {
    paths: paths,
    fallback: false
  };
};
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('tiskove-zpravy/[:pressRelease]');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  const pressReleaseSlug = ctx.params?.pressRelease as string;
  const pressReleaseDetail = await getPressReleaseDetail(pressReleaseSlug);
  if (!pressReleaseDetail) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pressReleaseDetail: pressReleaseDetail,
      ...(await getIntlProps(ctx, ['aboutUs', 'core', 'forJournalists']))
    }
  };
}) satisfies GetStaticProps<PageProps>;